import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Tooltip from "@mui/material/Tooltip";
import { useContext, useEffect, useRef, useState } from "react";
import { InfoCircle } from "react-bootstrap-icons";
import "../../../../Tooltip/styles/tooltip.css";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import { FormContext } from "../context/FormProvider";
import { handleKeyDown, handleShowEmailPlaceholder } from "../main-helper.js";
import * as Messages from "../messages/messages";
import "./styles/text-field.css";

// Component that renders an email field
const NewEmail = ({
  idProp,
  label,
  val,
  state,
  setState,
  req,
  debouncedSave,
  setIsLoading,
  autoData,
  disabledInput,
  disabledInputMessage,
  invalidData,
}) => {
  const emailRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [openHelper, setOpenHelper] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [placeholder, setPlaceholder] = useState("");
  const [suggestedEmail, setSuggestedEmail] = useState("");
  const [adjustMargin, setAdjustMargin] = useState(false);

  const { requestStatus, handleInputFocus, handleInputDisable } =
    useContext(FormContext);
  const { clientId, projectId, queryClient, userId } =
    useContext(DashboardContext);

  // Query the forms state (used to find email used in other fields)
  const formData = queryClient.getQueryData([
    "currentProject",
    clientId,
    projectId,
  ]);

  useEffect(() => {
    if (req) {
      setAdjustMargin(true);
    } else {
      setAdjustMargin(false);
    }
  }, [req]);

  // Checks if the email field matches an email used in another field
  useEffect(() => {
    handleShowEmailPlaceholder(
      idProp,
      state,
      formData,
      placeholder,
      setPlaceholder,
      setSuggestedEmail,
      showPlaceholder,
    );
  }, [showPlaceholder, state, idProp, formData, placeholder]);

  // Function to handle the email change
  const handleEmailChange = (e) => {
    e.target.value.trimStart();
    e.target.value.trimEnd();
    // no spaces should be allowed in email
    let value = e.target.value.trim().replace(/\s+/g, "");
    let newState = { ...state, [idProp]: value.toLowerCase() };
    setState(newState);

    if (!placeholder || !placeholder.startsWith(value)) {
      if (autoData && autoData.length > 0) {
        let match = autoData.find((email) => email.startsWith(value));
        if (match) {
          match = value + match.slice(value.length);
        }

        if (match && value !== "") {
          setPlaceholder(match);
          setShowPlaceholder(true);
        } else if (value === "" && suggestedEmail !== "") {
          setPlaceholder(suggestedEmail);
          setShowPlaceholder(true);
        } else {
          setPlaceholder("");
          setShowPlaceholder(false);
        }
      }
    } else if (suggestedEmail && suggestedEmail.slice(value.length)) {
      let existingMatch = value + suggestedEmail.slice(value.length);
      setPlaceholder(existingMatch);
      setShowPlaceholder(true);
    }

    if (debouncedSave && setIsLoading && requestStatus !== "A") {
      setIsLoading(true);
      debouncedSave.mutate(newState);
    }
  };

  return (
    <>
      <div className="text-info-container email-info-container">
        <FormControl
          variant="outlined"
          id="text-form"
          classes={{ root: "form-text-root" }}
          className={`email-form-control ${
            requestStatus === "RV" &&
            disabledInput === false &&
            invalidData &&
            (val === invalidData["initialValue"] || val === "")
              ? "revise-input"
              : requestStatus === "RV" &&
                  disabledInput === false &&
                  invalidData &&
                  val !== invalidData["initialValue"] &&
                  val !== ""
                ? "revised-input"
                : req
                  ? "required"
                  : ""
          }`}
        >
          <InputLabel
            id="text-field-label"
            htmlFor="text-input"
            classes={{ shrink: "text-field-label-shrink" }}
          >
            {`${label}`}
          </InputLabel>
          <OutlinedInput
            type="email"
            id="text-field"
            className={`${idProp}-input email-input`}
            ref={emailRef}
            label={`${label}`}
            value={val}
            onChange={handleEmailChange}
            onFocus={() => handleInputFocus(emailRef, setOpenHelper)}
            onSelect={() => {
              if (state[idProp] === "") {
                setOpen(true);
              } else {
                setOpen(false);
              }
              setShowPlaceholder(true);
              setOpenHelper(true);
            }}
            onBlur={() => {
              setOpen(false);
              setShowPlaceholder(false);
              setOpenHelper(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Spacebar" || e.key === " ") {
                e.preventDefault();
                return;
              }
              handleKeyDown(
                e,
                idProp,
                state,
                setState,
                autoData,
                placeholder,
                debouncedSave,
                setIsLoading,
                setPlaceholder,
                setShowPlaceholder,
                setOpen,
                setOpenHelper,
              );
            }}
            disabled={handleInputDisable(idProp, state, disabledInput, userId)}
            autoComplete="off"
            autoCapitalize="words"
            classes={{
              root: "text-field-root",
              notchedOutline: "text-notchedOutline",
              input: "text-field-input",
            }}
            endAdornment={
              <Tooltip
                id="clear-btn-tooltip"
                classes={{
                  tooltip: "tooltip-clear-btn",
                  popper: "popper-clear-btn",
                  tooltipPlacementTop: "tooltip-top",
                }}
                title={val === "" ? "" : "Clear Field"}
                placement="top"
              >
                <InputAdornment
                  className="text-adornment"
                  id="text-adornment"
                  position="end"
                  style={
                    val === "" ||
                    handleInputDisable(idProp, state, disabledInput, userId) ===
                      true
                      ? { visibility: "hidden" }
                      : { visibility: "visible" }
                  }
                >
                  <IconButton
                    style={
                      val === ""
                        ? {
                            pointerEvents: "none",
                            cursor: "default",
                          }
                        : {
                            pointerEvents: "auto",
                            cursor: "pointer",
                          }
                    }
                    aria-label="clear input field"
                    onClick={() => {
                      if (debouncedSave) {
                        let newState = {
                          ...state,
                          [idProp]: "",
                        };
                        setState(newState);
                        setIsLoading(true);
                        setPlaceholder("");
                        setShowPlaceholder(false);
                        debouncedSave.mutate(newState);
                      } else {
                        let newState = {
                          ...state,
                          [idProp]: "",
                        };
                        setState(newState);
                        setPlaceholder("");
                        setShowPlaceholder(false);
                      }
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    className="clear-icon-button"
                    id="clear-icon-button"
                    disabled={val === "" ? true : false}
                  >
                    <CloseIcon id="clear-input-icon" className="close-icon" />
                  </IconButton>
                </InputAdornment>
              </Tooltip>
            }
          />
        </FormControl>
        <input
          key={`${idProp}-placeholder`}
          className={`${
            req ? "predictive-placeholder shift" : "predictive-placeholder"
          }`}
          type="email"
          autoComplete="off"
          id="email-placeholder"
          value={placeholder}
          disabled={true}
        />
        <Tooltip
          id="ef-tooltip"
          classes={{
            tooltip: "text-field-tooltip",
            popper: "text-field-popper",
            arrow: "text-field-arrow",
            tooltipPlacementRight: "text-field-right",
          }}
          title={Messages[`${idProp}Message`]}
          placement={"top"}
          arrow
          open={open}
          sx={{
            backgroundColor: "rgb(0, 0, 0, 0.8) !important",
          }}
        >
          <InfoCircle
            className="text-info-circ"
            id={`info-${idProp}`}
            color="white"
            size={30}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
          />
        </Tooltip>
      </div>
    </>
  );
};

export default NewEmail;
